import type { ReactNode } from "react";
import { TRANSPORT_CAPACITY_TYPE_STATE } from "@zhihaoscm/meta";
import type { PlatClue } from "@/views/pages/logistics/cluesManage/platformCluesManage/utils";
import { $get, dateFormat } from "@/utils";
import { DATE_PICKER_FORMAT, DATETIME_PICKER_FORMAT, DEFAULT_VALUE } from "@/config";
import { QUOTE_IDENTITY } from "@/meta/logistics/shipNeed";
import { formatNumber } from "@zhihaoscm/utils";
import { formatAmountData } from "@/views/pages/purchasingManage/index";
import { formatSecondsToMinuteSecond } from "@/views/pages/logistics/cluesManage/dashboard/utils";
import type { CarrierShip } from "@zhihaoscm/entity";

type LegacyColumn = {
    label: string;
    prop: string;
    width?: number | string;
    minWidth?: number | string;
    fixed?: string;
    enum?: unknown;
    click?: (row: PlatClue) => void;
    formatter?: (row: PlatClue) => ReactNode;
};

// 注册用户信息表
export const userColumns: LegacyColumn[] = [
    {
        label: "序号",
        prop: "index",
        width: 100
    },
    {
        label: "注册用户ID",
        prop: "customer.id",
        width: 200
    },
    {
        label: "个人实名/组织机构",
        prop: "customer.institutionName",
        minWidth: 200,
        formatter: ({ customer }: PlatClue) => {
            return (customer && customer?.institutionName) || customer?.realName || DEFAULT_VALUE;
        }
    },
    {
        label: "注册手机号",
        prop: "customer.mobile",
        width: 150
    },
    {
        label: "注册时间",
        prop: "customer.createdTime",
        width: 150,
        formatter: ({ customer }: PlatClue) => {
            return customer?.createdTime ? dateFormat(customer.createdTime, DATETIME_PICKER_FORMAT) : DEFAULT_VALUE;
        }
    },
    {
        label: "提醒滞留时间",
        prop: "platClue.stayDurationSeconds",
        width: 150,
        formatter: (row: PlatClue) => {
            return row.platClue?.stayDurationSeconds
                ? formatSecondsToMinuteSecond(row.platClue.stayDurationSeconds)
                : DEFAULT_VALUE;
        }
    },
    {
        label: "处理状态",
        prop: "platClue.state",
        width: 100,
        enum: TRANSPORT_CAPACITY_TYPE_STATE.options
    },
    {
        label: "处理人",
        prop: "platClue.handlerName",
        width: 100
    },
    {
        label: "处理时间",
        prop: "platClue.handleTime",
        width: 150,
        formatter: (row: PlatClue) => {
            return dateFormat(row.platClue?.handleTime, DATETIME_PICKER_FORMAT);
        }
    },
    {
        label: "操作",
        prop: "operation",
        fixed: "right"
    }
];

// 找船需求信息表
export const demandColumns: LegacyColumn[] = [
    {
        label: "序号",
        prop: "index",
        width: 100
    },
    {
        label: "需求编号",
        prop: "shippingRequirementPlat.id",
        width: 200
    },
    {
        label: "货主",
        prop: "shippingRequirementPlat.ownerEnterprise.realName",
        minWidth: 250,
        formatter: (row: PlatClue) => {
            return (
                row.shippingRequirementPlat?.ownerEnterprise?.name ||
                row.shippingRequirementPlat?.ownerEnterprise?.realName ||
                DEFAULT_VALUE
            );
        }
    },
    {
        label: "运货吨数",
        prop: "shippingRequirementPlat.freightTons",
        width: 150
    },
    {
        label: "始发地",
        prop: "shippingRequirementPlat.sourcePortName",
        width: 150
    },
    {
        label: "目的地",
        prop: "shippingRequirementPlat.destinationPortName",
        width: 150
    },
    {
        label: "预期装卸日期",
        prop: "shippingRequirementPlat.loadDate",
        width: 200,
        formatter: (row: PlatClue) => {
            return `${dateFormat(row.shippingRequirementPlat?.loadDate)} +${$get(row, "shippingRequirementPlat.loadDays")}天`;
        }
    },
    {
        label: "装卸天数",
        prop: "shippingRequirementPlat.loadUnloadDays",
        width: 100
    },
    {
        label: "发布时间",
        prop: "shippingRequirementPlat.createdTime",
        width: 150,
        formatter: ({ shippingRequirementPlat }: PlatClue) => {
            return shippingRequirementPlat?.createdTime
                ? dateFormat(shippingRequirementPlat?.createdTime, DATETIME_PICKER_FORMAT)
                : DEFAULT_VALUE;
        }
    },
    {
        label: "提醒滞留时间",
        prop: "platClue.stayDurationSeconds",
        width: 150,
        formatter: (row: PlatClue) => {
            return row.platClue?.stayDurationSeconds
                ? formatSecondsToMinuteSecond(row.platClue.stayDurationSeconds)
                : DEFAULT_VALUE;
        }
    },
    {
        label: "处理状态",
        prop: "platClue.state",
        width: 100,
        enum: TRANSPORT_CAPACITY_TYPE_STATE.options
    },
    {
        label: "处理人",
        prop: "platClue.handlerName",
        width: 100
    },
    {
        label: "处理时间",
        prop: "platClue.handleTime",
        width: 150,
        formatter: (row: PlatClue) => {
            return row.platClue?.handleTime
                ? dateFormat(row.platClue.handleTime, DATETIME_PICKER_FORMAT)
                : DEFAULT_VALUE;
        }
    },
    {
        label: "操作",
        prop: "operation",
        fixed: "right"
    }
];

// 报价信息表
export const quoteColumns: LegacyColumn[] = [
    {
        label: "序号",
        prop: "index",
        width: 100
    },
    {
        label: "身份",
        prop: "shippingRequirementAccept.quoteIdentity",
        enum: QUOTE_IDENTITY.options,
        width: 100
    },
    {
        label: "报价需求编号",
        prop: "shippingRequirementAccept.requirementPlatId",
        minWidth: 150
    },
    {
        label: "承运商",
        prop: "shippingRequirementAccept.captainEnterprise.realName",
        width: 250,
        formatter: (row: PlatClue) => {
            if (QUOTE_IDENTITY.SHIP_OWNER.match(row.shippingRequirementAccept.quoteIdentity)) {
                // 船主
                return (
                    row.shippingRequirementAccept?.captainEnterprise?.name ||
                    row.shippingRequirementAccept?.captainEnterprise?.realName ||
                    DEFAULT_VALUE
                );
            } else if (QUOTE_IDENTITY.SHIP_AGENT.match(row.shippingRequirementAccept.quoteIdentity)) {
                // 船代
                return (
                    row.shippingRequirementAccept?.agentEnterprise?.name ||
                    row.shippingRequirementAccept?.agentEnterprise?.realName ||
                    DEFAULT_VALUE
                );
            }
        }
    },
    {
        label: "联系人",
        prop: "row.shippingRequirementAccept",
        width: 150,
        formatter: (row: PlatClue) => {
            return QUOTE_IDENTITY.SHIP_AGENT.match(row.shippingRequirementAccept.quoteIdentity)
                ? $get(row.shippingRequirementAccept, "agentContact")
                : $get(row.shippingRequirementAccept, "captainEnterprise.realName");
        }
    },
    {
        label: "联系电话",
        prop: "row.shippingRequirementAccept",
        width: 150,
        formatter: (row: PlatClue) => {
            return QUOTE_IDENTITY.SHIP_AGENT.match(row.shippingRequirementAccept.quoteIdentity)
                ? $get(row.shippingRequirementAccept, "agentMobile")
                : $get(row.shippingRequirementAccept, "captainEnterprise.mobile");
        }
    },
    {
        label: "报价单价（元/吨）",
        prop: "row.shippingRequirementAccept.shipInfos",
        width: 150,
        formatter: (row: PlatClue) => {
            return (row.shippingRequirementAccept.shipInfos?.[0] as CarrierShip).unitPrice
                ? formatNumber((row.shippingRequirementAccept.shipInfos?.[0] as CarrierShip).unitPrice, 2)
                : DEFAULT_VALUE;
        }
    },
    {
        label: "报价时间",
        prop: "row.shippingRequirementAccept.acceptTime",
        width: 200,
        formatter: (row: PlatClue) => {
            return row.shippingRequirementAccept?.acceptTime
                ? dateFormat(row.shippingRequirementAccept.acceptTime, DATETIME_PICKER_FORMAT)
                : DEFAULT_VALUE;
        }
    },
    {
        label: "装货吨数",
        prop: "row.shippingRequirementAccept.shipInfos",
        width: 100,
        formatter: (row: PlatClue) => {
            return (row.shippingRequirementAccept.shipInfos?.[0] as CarrierShip).ton
                ? formatNumber((row.shippingRequirementAccept.shipInfos[0] as CarrierShip).ton, 0)
                : DEFAULT_VALUE;
        }
    },
    {
        label: "预估运费（元）",
        prop: "row.shippingRequirementAccept.shipInfos",
        width: 150,
        formatter: (row: PlatClue) => {
            const { unitPrice, ton } = row.shippingRequirementAccept.shipInfos?.[0] as CarrierShip;
            return unitPrice && ton ? formatAmountData(Number(unitPrice * ton).toFixed(2)) : DEFAULT_VALUE;
        }
    },
    {
        label: "提醒滞留时间",
        prop: "platClue.stayDurationSeconds",
        width: 150,
        formatter: (row: PlatClue) => {
            return row.platClue.stayDurationSeconds
                ? formatSecondsToMinuteSecond(row.platClue.stayDurationSeconds)
                : DEFAULT_VALUE;
        }
    },
    {
        label: "处理状态",
        prop: "platClue.state",
        width: 100,
        enum: TRANSPORT_CAPACITY_TYPE_STATE.options
    },
    {
        label: "处理人",
        prop: "platClue.handlerName",
        width: 100
    },
    {
        label: "处理时间",
        prop: "platClue.handleTime",
        width: 150,
        formatter: (row: PlatClue) => {
            return row.platClue?.handleTime
                ? dateFormat(row.platClue.handleTime, DATETIME_PICKER_FORMAT)
                : DEFAULT_VALUE;
        }
    },
    {
        label: "操作",
        prop: "operation",
        fixed: "right"
    }
];

// 空船信息表
export const emptyShipColumns: LegacyColumn[] = [
    {
        label: "序号",
        prop: "index",
        width: 100
    },
    {
        label: "空船信息编号",
        prop: "emptyShipVo.emptyShip.id",
        minWidth: 200
    },
    {
        label: "承运商",
        prop: "emptyShipVo.carrier.realName",
        width: 250,
        formatter: (row: PlatClue) => {
            return row.emptyShipVo.carrier?.institutionName || row.emptyShipVo.carrier?.realName || DEFAULT_VALUE;
        }
    },
    {
        label: "实装吨数",
        prop: "emptyShipVo.emptyShip.tonCapacity",
        width: 150
    },
    {
        label: "空船始发地",
        prop: "emptyShipVo.originPorts",
        width: 200,
        formatter(row: PlatClue) {
            const portNames = row?.emptyShipVo.originPorts?.map(item => {
                return item.portName;
            });
            return portNames?.join("、");
        }
    },
    {
        label: "目的地",
        prop: "emptyShipVo.destPorts",
        width: 200,
        formatter(row: PlatClue) {
            const portNames = row?.emptyShipVo.destPorts?.map(item => {
                return item.portName;
            });
            return portNames?.join("、");
        }
    },
    {
        label: "预期空船时间",
        prop: "emptyShipVo.emptyShip.emptyStartTime",
        width: 300,
        formatter: (row: PlatClue) => {
            const startTime = dateFormat(row.emptyShipVo?.emptyShip?.emptyStartTime, DATE_PICKER_FORMAT);
            const endTime = dateFormat(row.emptyShipVo?.emptyShip?.emptyEndTime, DATE_PICKER_FORMAT);
            return startTime + " 至 " + endTime;
        }
    },
    {
        label: "发布人",
        prop: "emptyShipVo.emptyShip.publisherName",
        width: 100
    },
    {
        label: "发布时间",
        prop: "emptyShipVo.emptyShip.publishTime",
        width: 150,
        formatter: (row: PlatClue) => {
            return row.emptyShipVo.emptyShip?.publishTime
                ? dateFormat(row.emptyShipVo.emptyShip?.publishTime, DATETIME_PICKER_FORMAT)
                : DEFAULT_VALUE;
        }
    },
    {
        label: "提醒滞留时间",
        prop: "platClue.stayDurationSeconds",
        width: 150,
        formatter: (row: PlatClue) => {
            return row.platClue.stayDurationSeconds
                ? formatSecondsToMinuteSecond(row.platClue.stayDurationSeconds)
                : DEFAULT_VALUE;
        }
    },
    {
        label: "处理状态",
        prop: "platClue.state",
        width: 100,
        enum: TRANSPORT_CAPACITY_TYPE_STATE.options
    },
    {
        label: "处理人",
        prop: "platClue.handlerName",
        width: 100
    },
    {
        label: "处理时间",
        prop: "platClue.handleTime",
        width: 150,
        formatter: (row: PlatClue) => {
            return row.platClue?.handleTime
                ? dateFormat(row.platClue?.handleTime, DATETIME_PICKER_FORMAT)
                : DEFAULT_VALUE;
        }
    },
    {
        label: "操作",
        prop: "operation",
        fixed: "right"
    }
];
